import { OBJECT_ITEMS } from '../data/paintObjects';
import shortid from 'shortid';

/**
 * Get the rotated dimensions of an object based on its rotation angle
 * @param {Object} objectDef - Object definition with defaultWidth and defaultHeight
 * @param {number} rotation - Rotation angle (0, 90, 180, 270)
 * @returns {Object} - { width, height } after rotation
 */
export function getRotatedDimensions(objectDef, rotation) {
  const { defaultWidth, defaultHeight } = objectDef;
  
  // 90 and 270 degrees swap width and height
  if (rotation === 90 || rotation === 270) {
    return { width: defaultHeight, height: defaultWidth };
  }
  
  return { width: defaultWidth, height: defaultHeight };
}

/**
 * Get all grid cells occupied by an object instance
 * @param {Object} instance - Object instance with anchorX, anchorY, width, height, rotation
 * @returns {Array} - Array of { i, j } cell coordinates
 */
export function getOccupiedCells(instance) {
  const { anchorX, anchorY, width, height, rotation } = instance;
  const objectDef = { defaultWidth: width, defaultHeight: height };
  const { width: w, height: h } = getRotatedDimensions(objectDef, rotation);
  
  const cells = [];
  for (let i = 0; i < w; i++) {
    for (let j = 0; j < h; j++) {
      cells.push({ i: anchorX + i, j: anchorY + j });
    }
  }
  return cells;
}

/**
 * Check if an object can be placed at the given position
 * @param {number} anchorX - Grid X coordinate
 * @param {number} anchorY - Grid Y coordinate
 * @param {number} width - Object width in grid units
 * @param {number} height - Object height in grid units
 * @param {number} gridWidth - Total grid width
 * @param {number} gridHeight - Total grid height
 * @param {Object} gridObjects - Current grid objects (cell references)
 * @param {string} excludeInstanceId - Instance ID to exclude from collision check (for moving/rotating)
 * @returns {boolean} - True if object can be placed
 */
export function canPlaceObject(
  anchorX,
  anchorY,
  width,
  height,
  gridWidth,
  gridHeight,
  gridObjects,
  excludeInstanceId = null
) {
  // Check grid bounds
  if (anchorX < 0 || anchorY < 0) {
    return false;
  }
  if (anchorX + width > gridWidth || anchorY + height > gridHeight) {
    return false;
  }
  
  // Check if any cell is already occupied (by a different object)
  for (let i = 0; i < width; i++) {
    for (let j = 0; j < height; j++) {
      const cellKey = `${anchorX + i}-${anchorY + j}`;
      const occupyingInstanceId = gridObjects[cellKey];
      
      if (occupyingInstanceId && occupyingInstanceId !== excludeInstanceId) {
        return false;
      }
    }
  }
  
  return true;
}

/**
 * Create a new object instance
 * @param {string} objectType - Object type (e.g., 'bed-object')
 * @param {number} anchorX - Grid X coordinate
 * @param {number} anchorY - Grid Y coordinate
 * @param {number} rotation - Initial rotation (default 0)
 * @returns {Object} - New object instance
 */
export function createObjectInstance(objectType, anchorX, anchorY, rotation = 0) {
  const objectDef = OBJECT_ITEMS.find(o => o.color === objectType);
  
  if (!objectDef) {
    console.error('Unknown object type:', objectType);
    return null;
  }
  
  return {
    id: shortid.generate(),
    type: objectType,
    anchorX,
    anchorY,
    width: objectDef.defaultWidth,
    height: objectDef.defaultHeight,
    rotation
  };
}

/**
 * Place an object instance on the grid
 * @param {Object} instance - Object instance
 * @param {Object} gridObjects - Current grid objects (will be mutated)
 * @returns {Object} - Updated gridObjects
 */
export function placeObjectOnGrid(instance, gridObjects) {
  const cells = getOccupiedCells(instance);
  const updated = { ...gridObjects };
  
  cells.forEach(({ i, j }) => {
    const cellKey = `${i}-${j}`;
    updated[cellKey] = instance.id;
  });
  
  return updated;
}

/**
 * Remove an object instance from the grid
 * @param {Object} instance - Object instance
 * @param {Object} gridObjects - Current grid objects
 * @returns {Object} - Updated gridObjects
 */
export function removeObjectFromGrid(instance, gridObjects) {
  const cells = getOccupiedCells(instance);
  const updated = { ...gridObjects };
  
  cells.forEach(({ i, j }) => {
    const cellKey = `${i}-${j}`;
    if (updated[cellKey] === instance.id) {
      delete updated[cellKey];
    }
  });
  
  return updated;
}

/**
 * Get object instance at a specific grid cell
 * @param {number} i - Grid X coordinate
 * @param {number} j - Grid Y coordinate
 * @param {Object} gridObjects - Grid objects (cell references)
 * @param {Object} gridObjectInstances - Object instances
 * @returns {Object|null} - Object instance or null
 */
export function getObjectAtCell(i, j, gridObjects, gridObjectInstances) {
  const cellKey = `${i}-${j}`;
  const instanceId = gridObjects[cellKey];
  
  if (instanceId && gridObjectInstances[instanceId]) {
    return gridObjectInstances[instanceId];
  }
  
  return null;
}

/**
 * Rotate an object instance by 90 degrees clockwise
 * @param {Object} instance - Object instance
 * @param {number} gridWidth - Total grid width
 * @param {number} gridHeight - Total grid height
 * @param {Object} gridObjects - Current grid objects
 * @param {Object} gridObjectInstances - All object instances
 * @returns {Object|null} - { instance, gridObjects } or null if rotation is not possible
 */
export function rotateObjectInstance(instance, gridWidth, gridHeight, gridObjects, gridObjectInstances) {
  const newRotation = (instance.rotation + 90) % 360;
  const objectDef = { defaultWidth: instance.width, defaultHeight: instance.height };
  const { width, height } = getRotatedDimensions(objectDef, newRotation);
  
  // First, remove the object from the grid
  const clearedGrid = removeObjectFromGrid(instance, gridObjects);
  
  // Check if rotation is valid in the new orientation
  if (!canPlaceObject(
    instance.anchorX,
    instance.anchorY,
    width,
    height,
    gridWidth,
    gridHeight,
    clearedGrid,
    instance.id // Exclude self from collision check
  )) {
    // Rotation not possible - return null
    return null;
  }
  
  // Create updated instance with new rotation
  const updatedInstance = {
    ...instance,
    rotation: newRotation
  };
  
  // Place the rotated object back on the grid
  const updatedGrid = placeObjectOnGrid(updatedInstance, clearedGrid);
  
  return {
    instance: updatedInstance,
    gridObjects: updatedGrid
  };
}

/**
 * Check if a color/type is an object (vs terrain)
 * @param {string} color - Color/type string
 * @returns {boolean} - True if it's an object
 */
export function isObjectType(color) {
  if (typeof color !== 'string') return false;
  return OBJECT_ITEMS.some(obj => obj.color === color);
}

/**
 * Get object definition by type
 * @param {string} objectType - Object type (e.g., 'bed-object')
 * @returns {Object|null} - Object definition or null
 */
export function getObjectDefinition(objectType) {
  return OBJECT_ITEMS.find(o => o.color === objectType) || null;
}

